/**
 * BIGWA Core - IndexedDB Cache (L2)
 * 
 * Cache persistente usando IndexedDB.
 * Usado como segunda camada de cache (L2) para persistência entre sessões.
 */

import { ok, err, AsyncResult } from '../types/result';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Entrada do cache com metadados
 */
interface CacheEntry<T> {
  key: string;
  value: T;
  expiresAt: number | null; // null = nunca expira
  createdAt: number;
  updatedAt: number;
}

/**
 * Configuração do IndexedDB Cache
 */
export interface IndexedDBCacheConfig {
  /**
   * Nome do banco de dados
   */
  dbName: string;

  /**
   * Nome do object store
   */
  storeName?: string;

  /**
   * Versão do banco (para migrations)
   */
  version?: number;

  /**
   * TTL padrão em ms (null = nunca expira)
   */
  defaultTtl?: number | null;
}

// ============================================================================
// INDEXEDDB CACHE
// ============================================================================

/**
 * Cache persistente usando IndexedDB
 * 
 * @example
 * const cache = new IndexedDBCache<User>({ dbName: 'bigwa-cache' });
 * await cache.init();
 * 
 * await cache.set('user:123', user);
 * const cached = await cache.get('user:123');
 */
export class IndexedDBCache<T> {
  private db: IDBDatabase | null = null;
  private readonly dbName: string;
  private readonly storeName: string;
  private readonly version: number;
  private readonly defaultTtl: number | null;
  private initPromise: Promise<void> | null = null;

  constructor(config: IndexedDBCacheConfig) {
    this.dbName = config.dbName;
    this.storeName = config.storeName ?? 'cache';
    this.version = config.version ?? 1;
    this.defaultTtl = config.defaultTtl ?? null;
  }

  // ============================================================================
  // INICIALIZAÇÃO
  // ============================================================================

  /**
   * Inicializa o banco de dados
   * É chamado automaticamente nas operações, mas pode ser chamado manualmente
   */
  async init(): Promise<void> {
    if (this.db) return;
    
    // Evita múltiplas inicializações simultâneas
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.openDatabase();
    await this.initPromise;
  }

  private async openDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Verifica se IndexedDB está disponível
      if (!('indexedDB' in globalThis)) {
        reject(new Error('IndexedDB is not available'));
        return;
      }

      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Cria object store se não existir
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  /**
   * Garante que o banco está inicializado antes de operações
   */
  private async ensureInitialized(): Promise<IDBDatabase> {
    await this.init();
    
    if (!this.db) {
      throw new Error('IndexedDB not initialized');
    }
    
    return this.db;
  }

  // ============================================================================
  // OPERAÇÕES BÁSICAS
  // ============================================================================

  /**
   * Obtém valor do cache
   */
  async get(key: string): AsyncResult<T | null> {
    try {
      const db = await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);

        request.onerror = () => {
          resolve(err(new Error(`Failed to get from IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          
          if (!entry) {
            resolve(ok(null));
            return;
          }

          // Verifica expiração
          if (entry.expiresAt && Date.now() > entry.expiresAt) {
            // Remove entrada expirada (async, não aguarda)
            this.delete(key);
            resolve(ok(null));
            return;
          }

          resolve(ok(entry.value));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Define valor no cache
   */
  async set(key: string, value: T, ttl?: number | null): AsyncResult<void> {
    try {
      const db = await this.ensureInitialized();
      const now = Date.now();
      const actualTtl = ttl !== undefined ? ttl : this.defaultTtl;
      
      const entry: CacheEntry<T> = {
        key,
        value,
        expiresAt: actualTtl ? now + actualTtl : null,
        createdAt: now,
        updatedAt: now,
      };

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put(entry);

        request.onerror = () => {
          resolve(err(new Error(`Failed to set in IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          resolve(ok(undefined));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Remove valor do cache
   */
  async delete(key: string): AsyncResult<boolean> {
    try {
      const db = await this.ensureInitialized();

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);

        request.onerror = () => {
          resolve(err(new Error(`Failed to delete from IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          resolve(ok(true));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Verifica se chave existe (e não está expirada)
   */
  async has(key: string): AsyncResult<boolean> {
    const result = await this.get(key);
    
    if (!result.success) {
      return result;
    }
    
    return ok(result.data !== null);
  }

  /**
   * Limpa todo o cache
   */
  async clear(): AsyncResult<void> {
    try {
      const db = await this.ensureInitialized();

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onerror = () => {
          resolve(err(new Error(`Failed to clear IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          resolve(ok(undefined));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  // ============================================================================
  // OPERAÇÕES AVANÇADAS
  // ============================================================================

  /**
   * Obtém múltiplos valores de uma vez
   */
  async getMany(keys: string[]): AsyncResult<Map<string, T>> {
    try {
      const results = new Map<string, T>();
      
      for (const key of keys) {
        const result = await this.get(key);
        if (result.success && result.data !== null) {
          results.set(key, result.data);
        }
      }
      
      return ok(results);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Define múltiplos valores de uma vez
   */
  async setMany(entries: Array<{ key: string; value: T; ttl?: number | null }>): AsyncResult<void> {
    try {
      for (const { key, value, ttl } of entries) {
        const result = await this.set(key, value, ttl);
        if (!result.success) {
          return result;
        }
      }
      
      return ok(undefined);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Remove todas as chaves que começam com prefixo
   */
  async deleteByPrefix(prefix: string): AsyncResult<number> {
    try {
      const keys = await this.keys();
      
      if (!keys.success) {
        return keys;
      }

      const keysToDelete = keys.data.filter((key) => key.startsWith(prefix));
      let deleted = 0;

      for (const key of keysToDelete) {
        const result = await this.delete(key);
        if (result.success) {
          deleted++;
        }
      }

      return ok(deleted);
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Lista todas as chaves
   */
  async keys(): AsyncResult<string[]> {
    try {
      const db = await this.ensureInitialized();

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.getAllKeys();

        request.onerror = () => {
          resolve(err(new Error(`Failed to get keys from IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          resolve(ok(request.result as string[]));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Remove todas as entradas expiradas
   */
  async cleanup(): AsyncResult<number> {
    try {
      const db = await this.ensureInitialized();
      const now = Date.now();

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const index = store.index('expiresAt');
        
        // Busca todas as entradas com expiresAt <= now
        const range = IDBKeyRange.upperBound(now);
        const request = index.openCursor(range);
        
        let deleted = 0;

        request.onerror = () => {
          resolve(err(new Error(`Failed to cleanup IndexedDB: ${request.error?.message}`)));
        };

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          
          if (cursor) {
            const entry = cursor.value as CacheEntry<T>;
            
            // Só deleta se realmente expirou (expiresAt não é null)
            if (entry.expiresAt && entry.expiresAt <= now) {
              cursor.delete();
              deleted++;
            }
            
            cursor.continue();
          } else {
            resolve(ok(deleted));
          }
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  /**
   * Retorna número aproximado de entradas
   */
  async size(): AsyncResult<number> {
    try {
      const db = await this.ensureInitialized();

      return new Promise((resolve) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.count();

        request.onerror = () => {
          resolve(err(new Error(`Failed to count IndexedDB entries: ${request.error?.message}`)));
        };

        request.onsuccess = () => {
          resolve(ok(request.result));
        };
      });
    } catch (error) {
      return err(error as Error);
    }
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  /**
   * Fecha conexão com o banco
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }

  /**
   * Deleta o banco de dados completamente
   */
  async destroy(): AsyncResult<void> {
    this.close();

    return new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(this.dbName);

      request.onerror = () => {
        resolve(err(new Error(`Failed to delete database: ${request.error?.message}`)));
      };

      request.onsuccess = () => {
        resolve(ok(undefined));
      };
    });
  }
}

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Cria instância de IndexedDBCache
 */
export const createIndexedDBCache = <T>(
  config: IndexedDBCacheConfig
): IndexedDBCache<T> => {
  return new IndexedDBCache<T>(config);
};
